import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Loader2, Send } from "lucide-react";

const SECTIONS = [
  {
    title: "Why now",
    questions: [
      "What finally happened in your life that made you say, \"I can't keep doing things this way anymore\"?",
      "What is the heaviest thing you are carrying right now that you just want to lay down?",
      "If you walk away from this immersion with just one big relief, what does that need to be?",
      "What have you been trying to fix on your own that still isn't working?",
      "In simple words, what does feeling \"braced\" or \"on edge\" look like in your daily life?",
    ],
  },
  {
    title: "Where you come from",
    questions: [
      "When you were growing up, what job did you have to take on to keep the peace or stay safe at home?",
      "What did the adults around you teach you about feelings, needing help, or taking a break?",
      "When you were scared, sad, or overwhelmed as a child, who came to comfort you?",
      "Where in your life today are you still trying to be \"good,\" quiet, or helpful just to feel safe?",
      "What hard habit did you pick up from your family—like holding everything in or never asking for help?",
    ],
  },
  {
    title: "Your body",
    questions: [
      "Where in your body do you feel the most tightness, ache, or fatigue right now?",
      "What happens to your body when you push past your limit and run completely out of fuel?",
      "When you try to sit still or relax, what bad feeling or thought pops up right away?",
      "Where in your day does your body stay tense, even when you know nothing bad is happening?",
      "What is the main thing your body needs right now to feel rested?",
    ],
  },
  {
    title: "Your inner world",
    questions: [
      "What thought keeps spinning in your head over and over, exhausting your mind?",
      "What are you secretly struggling with that you haven't told anyone yet?",
      "What old way of protecting yourself used to work, but is now just making life harder?",
      "What bad thing do you think will happen if you finally take a real break?",
      "What feeling do you wake up with most mornings before you even get out of bed?",
    ],
  },
  {
    title: "Your relationships",
    questions: [
      "Whose problems or feelings are you trying to manage that aren't actually your responsibility?",
      "Has being \"the strong one\" made you feel lonely or like no one is there for you?",
      "What are you afraid will fall apart if you stop holding everything together?",
      "Who in your life do you feel you have to put your guard up around?",
      "What is a \"no\" you desperately need to say to someone, but are too afraid to say out loud?",
    ],
  },
  {
    title: "Daily life & what you need",
    questions: [
      "What part of your normal daily routine feels the most exhausting to keep up with right now?",
      "What rule about yourself are you finally ready to stop following?",
      "What does a safe, peaceful day actually look like to you?",
      "What do you need from us so you can feel safe enough to drop your guard here?",
      "What is the main thing you want to make sure we talk about during our time together?",
    ],
  },
];

const ALL_QUESTIONS = SECTIONS.flatMap((s) => s.questions);

export default function ImmersionIntake({ onSubmitted }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const topRef = useRef(null);

  const setAns = (idx, val) => setAnswers((a) => ({ ...a, [idx]: val }));

  const firstEmpty = useMemo(() => {
    if (!fullName.trim()) return "name";
    if (!email.trim() || !email.includes("@")) return "email";
    if (!phone.trim()) return "phone";
    for (let i = 0; i < ALL_QUESTIONS.length; i++) {
      if (!(answers[i] || "").trim()) return `q-${i}`;
    }
    return null;
  }, [fullName, email, phone, answers]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (firstEmpty) {
      setErr("Please complete every field — each answer helps Casey meet you where you are.");
      const el = document.querySelector(`[data-testid="intake-${firstEmpty}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSending(true);
    try {
      const responses = ALL_QUESTIONS.map((q, i) => ({ question: q, answer: (answers[i] || "").trim() }));
      await axios.post(`${API}/offerings/immersion/apply`, {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        responses,
      });
      onSubmitted?.();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Something interrupted the sending. Please try again.");
    } finally {
      setSending(false);
    }
  };

  let qIndex = -1;
  return (
    <form onSubmit={submit} ref={topRef} className="mt-8 rounded-2xl bg-ivory-50 p-6 shadow-sm ring-1 ring-ivory-400/60 sm:p-8" data-testid="immersion-intake-form">
      <p className="font-display text-xl text-ink">Before you begin — a quiet inventory</p>
      <p className="mt-2 text-sm leading-relaxed text-ink-soft">
        These questions help Casey understand where you are before your first session. Take your time — every field is required, and your answers are sent only to Casey.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">Full name *</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="intake-name"
            className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">Email *</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="intake-email"
            className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">Phone *</span>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="intake-phone"
            className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
        </label>
      </div>

      {SECTIONS.map((section, si) => (
        <div key={si} className="mt-8">
          <p className="kicker text-terracotta">{section.title}</p>
          <div className="mt-3 space-y-5">
            {section.questions.map((q) => {
              qIndex += 1;
              const idx = qIndex;
              return (
                <label key={idx} className="block">
                  <span className="mb-1.5 block text-sm leading-snug text-ink">
                    <span className="mr-1 text-ink-muted">{idx + 1}.</span>{q} <span className="text-terracotta">*</span>
                  </span>
                  <textarea
                    value={answers[idx] || ""}
                    onChange={(e) => setAns(idx, e.target.value)}
                    rows={2}
                    data-testid={`intake-q-${idx}`}
                    className="w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50"
                  />
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {err && <p className="mt-5 text-sm text-terracotta" data-testid="intake-error">{err}</p>}
      <button type="submit" disabled={sending} data-testid="intake-submit"
        className="debossed-btn mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-ink disabled:opacity-50">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to Casey & continue
      </button>
    </form>
  );
}
